import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  SSO_DOC_CATEGORIES, validarFechasDocumento, cambiosDocumento, type SSODocCategory,
  ITEM_SUGERIDO_POR_CATEGORIA, ordenarOpcionesItem, avisoVigencia,
  acceptSSO, tipoArchivoSSO, archivoParaSubir, validarArchivoSSO, mensajeErrorSubida, MENSAJE_ERROR_SUBIDA,
  SSO_KIND_LABELS, SSO_KINDS_ADICIONALES, type SSOKindArchivo, esTablaInexistente,
  ITEMS_CON_AUTOCAD, itemAceptaAutocad, esArchivoAutocad, MENSAJE_AUTOCAD_SOLO_ITEM, rutaStorageDesdeUrl,
  tamanoLegible, kbParaGuardar, planBorradoDocumento, resumenBorrado, rutasNoBorradas, type PasoBorrado, type VersionDocumento,
} from '../lib/sso-documentos';
import { nombreSeguroStorage } from '../lib/nombre-storage';
import { aplicarVigenciaSugerida } from '../lib/vigencias-sso';
import { type InformeGA07 } from '../lib/ga07-meses';
import { logAudit } from '../auditLog';
import { ITEMS_SP } from '../lib/servicios-permanentes';
import BuscadorOpciones from '../components/BuscadorOpciones';
import MesesGA07 from '../components/MesesGA07';
import EnlaceArchivo from '../components/EnlaceArchivo';
import { abrirArchivo } from '../lib/archivos';

// ── Types ──────────────────────────────────────────────────────────────────────

// Servicios Permanentes (bloque 7, C2): evidencia opcional, se sube a mano.
const OPCIONES_SP: { id: string; label: string }[] = ITEMS_SP.map(sp => ({ id: sp.id, label: `${sp.id} — ${sp.titulo}` }));

// Ítems del checklist Anexo 1 que ya tienen la funcionalidad de vínculo documental construida.
// Filtro: incluye GA-02 (el RHS aprobado se sube a mano desde el bloque 5), GT-01 y
// GT-04 (generados desde el Constructor / Matriz de Riesgos), GT-06 (PDF de RULA, REBA,
// NIOSH, Snook-Ciriello y ROSA), GT-13 (automático desde Etiquetado SGA) y TH-09
// (automático desde Capacitaciones); estos últimos no se suben a mano.
const CHECKLIST_ITEM_FILTER_OPTIONS: { id: string; label: string }[] = [
  { id: 'GA-01', label: 'GA-01 — Plan de Prevención de Riesgos Laborales' },
  { id: 'GA-02', label: 'GA-02 — Reglamento de Higiene y Seguridad' },
  { id: 'GA-03', label: 'GA-03 — Política de seguridad y salud socializada' },
  { id: 'GA-04', label: 'GA-04 — Registro del Monitor SST en SUT' },
  { id: 'GA-05', label: 'GA-05 — Registro del Técnico SST en SUT' },
  { id: 'GA-06', label: 'GA-06 — Registro del Servicio Externo SST en SUT' },
  { id: 'GA-07', label: 'GA-07 — Informe de actividades del Técnico o Servicio Externo' },
  { id: 'GA-08', label: 'GA-08 — Registro del profesional médico en SUT' },
  { id: 'GA-09', label: 'GA-09 — Registro del Delegado SST en SUT' },
  { id: 'GA-10', label: 'GA-10 — Registro del Comité SST en SUT' },
  { id: 'GA-11', label: 'GA-11 — Informe de gestión del Organismo Paritario' },
  { id: 'GA-12', label: 'GA-12 — Procedimientos para actividades simultáneas' },
  { id: 'GT-01', label: 'GT-01 — Diagrama de flujo de procesos' },
  { id: 'GT-03', label: 'GT-03 — Mapa de riesgos' },
  { id: 'GT-04', label: 'GT-04 — Matriz de Riesgos por puesto de trabajo' },
  { id: 'GT-05', label: 'GT-05 — Informe de medición de agentes físicos, químicos y biológicos' },
  { id: 'GT-06', label: 'GT-06 — Informe de evaluación de riesgos ergonómicos y psicosociales' },
  { id: 'GT-13', label: 'GT-13 — Etiquetado de agentes químicos en español' },
  { id: 'GT-15', label: 'GT-15 — Higiene Industrial: Agentes Físicos' },
  { id: 'GT-16', label: 'GT-16 — Higiene Industrial: Agentes Químicos' },
  { id: 'GT-17', label: 'GT-17 — Higiene Industrial: Agentes Biológicos' },
  { id: 'TH-03', label: 'TH-03 — Certificación trabajos de alto riesgo — Construcción' },
  { id: 'TH-04', label: 'TH-04 — Certificación trabajos de alto riesgo — Energía eléctrica' },
  { id: 'TH-07', label: 'TH-07 — Campañas de comunicación SST' },
  { id: 'TH-09', label: 'TH-09 — Registro de asistencia a capacitaciones' },
  { id: 'PO-06', label: 'PO-06 — Procedimiento de investigación de accidentes' },
  { id: 'PO-11', label: 'PO-11 — Procedimiento de investigación de enfermedades profesionales' },
  { id: 'PO-19', label: 'PO-19 — Programa de mantenimiento de instalaciones y equipos' },
  { id: 'PO-20', label: 'PO-20 — Ejecución del programa de mantenimiento' },
  ...OPCIONES_SP,
];

// Selector de subida manual: sin GT-01, GT-04, GT-06, GT-13 ni TH-09, que se resuelven
// solos desde sus módulos. GA-02 sí: el RHS aprobado por el Ministerio se sube como
// documento externo (el generador del RHS se quitó; bloque 5, B3). GA-04 a GA-10:
// el comprobante del registro en el SUT (bloque 5b, G).
const CHECKLIST_ITEM_UPLOAD_BASE: { id: string; label: string }[] = [
  { id: 'GA-01', label: 'GA-01 — Plan de Prevención de Riesgos Laborales' },
  { id: 'GA-02', label: 'GA-02 — Reglamento de Higiene y Seguridad (RHS aprobado)' },
  { id: 'GA-03', label: 'GA-03 — Política de seguridad y salud socializada' },
  { id: 'GA-04', label: 'GA-04 — Registro del Monitor SST en SUT' },
  { id: 'GA-05', label: 'GA-05 — Registro del Técnico SST en SUT' },
  { id: 'GA-06', label: 'GA-06 — Registro del Servicio Externo SST en SUT' },
  { id: 'GA-07', label: 'GA-07 — Informe de actividades del Técnico o Servicio Externo' },
  { id: 'GA-08', label: 'GA-08 — Registro del profesional médico en SUT' },
  { id: 'GA-09', label: 'GA-09 — Registro del Delegado SST en SUT' },
  { id: 'GA-10', label: 'GA-10 — Registro del Comité SST en SUT' },
  { id: 'GA-11', label: 'GA-11 — Informe de gestión del Organismo Paritario' },
  { id: 'GA-12', label: 'GA-12 — Procedimientos para actividades simultáneas' },
  { id: 'GT-03', label: 'GT-03 — Mapa de riesgos' },
  { id: 'GT-05', label: 'GT-05 — Informe de medición de agentes físicos, químicos y biológicos' },
  { id: 'GT-15', label: 'GT-15 — Higiene Industrial: Agentes Físicos' },
  { id: 'GT-16', label: 'GT-16 — Higiene Industrial: Agentes Químicos' },
  { id: 'GT-17', label: 'GT-17 — Higiene Industrial: Agentes Biológicos' },
  { id: 'TH-03', label: 'TH-03 — Certificación trabajos de alto riesgo — Construcción' },
  { id: 'TH-04', label: 'TH-04 — Certificación trabajos de alto riesgo — Energía eléctrica' },
  { id: 'TH-07', label: 'TH-07 — Campañas de comunicación SST' },
  { id: 'PO-06', label: 'PO-06 — Procedimiento de investigación de accidentes' },
  { id: 'PO-11', label: 'PO-11 — Procedimiento de investigación de enfermedades profesionales' },
  { id: 'PO-19', label: 'PO-19 — Programa de mantenimiento de instalaciones y equipos' },
  { id: 'PO-20', label: 'PO-20 — Ejecución del programa de mantenimiento' },
  ...OPCIONES_SP,
];

// Los ítems que aceptan AutoCAD (bloque 5d, G) se pueden subir a mano aunque se
// generen en otro módulo: GT-04 (Matriz de Riesgos) es el que pidió Kevin.
const CHECKLIST_ITEM_UPLOAD_OPTIONS: { id: string; label: string }[] = [
  ...CHECKLIST_ITEM_UPLOAD_BASE,
  ...ITEMS_CON_AUTOCAD
    .filter(id => !CHECKLIST_ITEM_UPLOAD_BASE.some(o => o.id === id))
    .map(id => CHECKLIST_ITEM_FILTER_OPTIONS.find(o => o.id === id) ?? { id, label: id }),
];

interface SSODocument {
  id: string;
  company_id: string;
  category: SSODocCategory;
  title: string;
  description: string | null;
  file_url: string;
  file_name: string;
  file_size_kb: number | null;
  uploaded_by: string | null;
  expiry_date: string | null;
  approved_from: string | null;
  created_at: string;
  updated_at: string;
  version: number;
  parent_document_id: string | null;
  is_current: boolean;
  checklist_item_id: string | null;
}

// Fila de sso_document_files (M11, bloque 5b H). file_path es la ruta en el bucket.
interface SSODocumentFile {
  id: string;
  document_id: string;
  file_path: string;
  file_name: string;
  file_size_kb: number | null;
  kind: SSOKindArchivo;
  created_at: string;
}

// Archivo elegido en el modal y todavía no subido.
interface ArchivoNuevo { key: number; file: File; kind: Exclude<SSOKindArchivo, 'principal'> }

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<SSODocCategory, string> = {
  reglamento:     'Reglamento',
  politica:       'Política',
  informe:        'Informe',
  registro:       'Registro',
  acta:           'Acta',
  permiso:        'Permiso',
  // Solo cambia la etiqueta (bloque 5d, A2); el valor sigue siendo 'certificado'.
  certificado:    'Certificado / Certificación',
  procedimiento:  'Procedimiento',
  plan_emergencia:'Plan de emergencia',
  matriz_riesgos: 'Matriz de riesgos',
  otro:           'Otro',
};

const CATEGORY_COLORS: Record<SSODocCategory, { bg: string; color: string; border: string }> = {
  reglamento:     { bg: 'rgba(139,92,246,0.12)',  color: '#a78bfa', border: 'rgba(139,92,246,0.3)'  },
  politica:       { bg: 'rgba(20,184,166,0.12)',  color: '#2dd4bf', border: 'rgba(20,184,166,0.3)'  },
  informe:        { bg: 'rgba(236,72,153,0.12)',  color: '#f472b6', border: 'rgba(236,72,153,0.3)'  },
  registro:       { bg: 'rgba(14,165,233,0.12)',  color: '#38bdf8', border: 'rgba(14,165,233,0.3)'  },
  acta:           { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa', border: 'rgba(59,130,246,0.3)'  },
  permiso:        { bg: 'rgba(245,158,11,0.12)',  color: '#fbbf24', border: 'rgba(245,158,11,0.3)'  },
  certificado:    { bg: 'rgba(16,185,129,0.12)',  color: '#34d399', border: 'rgba(16,185,129,0.3)'  },
  procedimiento:  { bg: 'rgba(99,102,241,0.12)',  color: '#818cf8', border: 'rgba(99,102,241,0.3)'  },
  plan_emergencia:{ bg: 'rgba(239,68,68,0.12)',   color: '#f87171', border: 'rgba(239,68,68,0.3)'   },
  matriz_riesgos: { bg: 'rgba(249,115,22,0.12)',  color: '#fb923c', border: 'rgba(249,115,22,0.3)'  },
  otro:           { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
};

// Opciones del buscador de Categoría en Subir y Editar (bloque 5d, A3).
const OPCIONES_CATEGORIA = SSO_DOC_CATEGORIES.map(c => ({ value: c, label: CATEGORY_LABELS[c] }));

// ── Helpers ────────────────────────────────────────────────────────────────────

function expiryStatus(date: string | null): 'expired' | 'warning' | 'ok' | null {
  if (!date) return null;
  const diff = (new Date(date).getTime() - Date.now()) / 86400000;
  if (diff < 0)  return 'expired';
  if (diff < 30) return 'warning';
  return 'ok';
}

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-EC');
}

// E3 (bloque 6): menos de 1 KB se muestra "< 1 KB" (tamanoLegible en sso-documentos.ts).
const formatSize = tamanoLegible;

// Al editar, un documento generado por un módulo (GT-01, GT-06…) conserva su ítem
// aunque no esté en el selector de subida.
function opcionesItemEdicion(actual: string): { id: string; label: string }[] {
  if (!actual || CHECKLIST_ITEM_UPLOAD_OPTIONS.some(o => o.id === actual)) return CHECKLIST_ITEM_UPLOAD_OPTIONS;
  const extra = CHECKLIST_ITEM_FILTER_OPTIONS.find(o => o.id === actual) ?? { id: actual, label: actual };
  return [...CHECKLIST_ITEM_UPLOAD_OPTIONS, extra];
}

function fileExtension(name: string) {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function urlArchivo(path: string) {
  return supabase.storage.from('sso-documents').getPublicUrl(path).data.publicUrl;
}

// Sube un archivo al bucket con nombre seguro (bloque 5b, A) y el tipo según
// la extensión (B; .dwg y .dxf solo en los ítems con AutoCAD, 5d G). El tipo va
// en el propio File (archivoParaSubir): con un File, upload() no usa contentType.
// Devuelve la ruta o lanza el error de Storage.
async function subirAlBucket(companyId: string, file: File, itemId: string): Promise<string> {
  const path = `${companyId}/${Date.now()}_${nombreSeguroStorage(file.name)}`;
  const { error } = await supabase.storage
    .from('sso-documents')
    .upload(path, archivoParaSubir(file, itemId), { upsert: true, contentType: tipoArchivoSSO(file.name, itemId) ?? undefined });
  if (error) throw error;
  return path;
}

let siguienteKey = 1;

// ── Component ──────────────────────────────────────────────────────────────────

export default function SSODocumentsPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const canUpload = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  const [docs, setDocs]       = useState<SSODocument[]>([]);
  const [loading, setLoading] = useState(true);
  // Mensaje fuera de los modales (el documento se guardó, pero algo menor falló).
  const [aviso, setAviso]     = useState('');

  // Varios archivos por documento (H). null = todavía no se sabe si M11 está
  // aplicada; false = sin la tabla, la pantalla trabaja con un solo archivo.
  const [archivosDisponibles, setArchivosDisponibles] = useState<boolean | null>(null);
  const [archivosPorDoc, setArchivosPorDoc] = useState<Record<string, SSODocumentFile[]>>({});

  // Filters
  const [search,      setSearch]      = useState('');
  const [filterCat,   setFilterCat]   = useState<SSODocCategory | ''>('');
  const [filterItem,  setFilterItem]  = useState<string>('');
  const [isDeepLinkFilter, setIsDeepLinkFilter] = useState(false);
  // Abrir Subir al llegar del Checklist; aprobadoDesde viene de un mes de GA-07 (5d, D).
  const [subirPendiente, setSubirPendiente] = useState<{ aprobadoDesde: string } | null>(null);
  // Todos los GA-07 (también los archivados) para la fila de meses (5d, D).
  const [informesGA07, setInformesGA07] = useState<InformeGA07[]>([]);

  // Upload modal
  const [showModal,   setShowModal]   = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [modalError,  setModalError]  = useState('');
  const [form, setForm] = useState({
    title: '', category: '' as SSODocCategory | '', description: '', approved_from: '', expiry_date: '', checklist_item_id: '',
  });
  const [vig, setVig]       = useState({ auto: false, nota: '' });
  const [file, setFile]     = useState<File | null>(null);
  const [extras, setExtras] = useState<ArchivoNuevo[]>([]);
  const fileRef             = useRef<HTMLInputElement>(null);

  // Edit (B2); desde el bloque 5d (C) también reemplaza el archivo principal.
  const [editTarget, setEditTarget] = useState<SSODocument | null>(null);
  const [editPrincipal, setEditPrincipal] = useState<File | null>(null);
  const editFileRef = useRef<HTMLInputElement>(null);
  const [editForm,   setEditForm]   = useState({ title: '', category: '' as SSODocCategory | '', checklist_item_id: '', approved_from: '', expiry_date: '' });
  const [editVig,    setEditVig]    = useState({ auto: false, nota: '' });
  const [editExtras, setEditExtras] = useState<ArchivoNuevo[]>([]);
  const [editQuitar, setEditQuitar] = useState<string[]>([]);
  const [editError,  setEditError]  = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete confirm
  const [deleteTarget,  setDeleteTarget]  = useState<SSODocument | null>(null);
  const [deleteStep,    setDeleteStep]    = useState(1);
  const [deleteError,   setDeleteError]   = useState('');
  const [deletePlan,    setDeletePlan]    = useState<PasoBorrado[] | null>(null);
  const [deleting,      setDeleting]      = useState(false);

  // Version history
  const [historyTarget,  setHistoryTarget]  = useState<SSODocument | null>(null);
  const [historyDocs,    setHistoryDocs]    = useState<SSODocument[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Deep link desde ChecklistPage: mismo patrón que ss_init_tab en Dashboard.tsx —
  // leer una vez al montar y limpiar inmediatamente. ss_init_subir (bloque 5b, G)
  // abre además el modal de subida con el ítem ya elegido; ss_init_aprobado_desde
  // (5d, D: un mes vacío de GA-07) llena "Aprobado desde".
  useEffect(() => {
    const stored = sessionStorage.getItem('ss_init_checklist_item');
    const subir  = sessionStorage.getItem('ss_init_subir');
    const aprobadoDesde = sessionStorage.getItem('ss_init_aprobado_desde') ?? '';
    sessionStorage.removeItem('ss_init_subir');
    sessionStorage.removeItem('ss_init_aprobado_desde');
    if (stored) {
      sessionStorage.removeItem('ss_init_checklist_item');
      setFilterItem(stored);
      setIsDeepLinkFilter(true);
      if (subir) setSubirPendiente({ aprobadoDesde });
    }
  }, []);

  // El rol llega después del primer render: el modal se abre cuando se sabe que puede subir.
  useEffect(() => {
    if (subirPendiente && canUpload && filterItem) {
      setSubirPendiente(null);
      abrirSubida(filterItem, subirPendiente.aprobadoDesde || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subirPendiente, canUpload, filterItem]);

  const loadDocs = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('sso_documents')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_current', true)
      .order('created_at', { ascending: false });
    if (error) console.error('Error al cargar documentos SSO:', error.message);
    setDocs((data ?? []) as SSODocument[]);

    const { data: ga07, error: ga07Err } = await supabase
      .from('sso_documents')
      .select('id, file_url, approved_from, is_current, created_at')
      .eq('company_id', companyId)
      .eq('checklist_item_id', 'GA-07');
    if (ga07Err) console.error('Error al cargar los informes GA-07:', ga07Err.message);
    setInformesGA07((ga07 ?? []) as InformeGA07[]);

    const { data: archivos, error: archErr } = await supabase
      .from('sso_document_files')
      .select('id, document_id, file_path, file_name, file_size_kb, kind, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });
    if (archErr) {
      if (!esTablaInexistente(archErr)) console.error('Error al cargar archivos de documentos SSO:', archErr.message);
      setArchivosDisponibles(false);
      setArchivosPorDoc({});
    } else {
      const porDoc: Record<string, SSODocumentFile[]> = {};
      ((archivos ?? []) as SSODocumentFile[]).forEach(a => { (porDoc[a.document_id] ??= []).push(a); });
      setArchivosDisponibles(true);
      setArchivosPorDoc(porDoc);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = docs.filter(d => {
    if (filterCat && d.category !== filterCat) return false;
    if (filterItem && d.checklist_item_id !== filterItem) return false;
    if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ── Formulario: categoría → ítem sugerido (E) y vigencia sugerida (F) ───────

  function cambiarForm(patch: Partial<typeof form>) {
    const base = { ...form, ...patch };
    if (patch.category && !base.checklist_item_id) {
      base.checklist_item_id = ITEM_SUGERIDO_POR_CATEGORIA[patch.category] ?? '';
    }
    if ('expiry_date' in patch) { setForm(base); setVig({ auto: false, nota: '' }); return; }
    const r = aplicarVigenciaSugerida(base, vig.auto);
    setForm(r.form);
    setVig({ auto: r.auto, nota: r.nota });
  }

  function cambiarEditForm(patch: Partial<typeof editForm>) {
    const base = { ...editForm, ...patch };
    if ('expiry_date' in patch) { setEditForm(base); setEditVig({ auto: false, nota: '' }); return; }
    const r = aplicarVigenciaSugerida(base, editVig.auto);
    setEditForm(r.form);
    setEditVig({ auto: r.auto, nota: r.nota });
  }

  function abrirSubida(item?: string, aprobadoDesde?: string) {
    setModalError('');
    if (item || aprobadoDesde) {
      cambiarForm({
        ...(item ? { checklist_item_id: item } : {}),
        ...(aprobadoDesde ? { approved_from: aprobadoDesde } : {}),
      });
    }
    setShowModal(true);
  }

  // "Subir nueva versión" (5d, C4): mismo título y categoría, así el documento
  // actual pasa al historial en vez de reemplazarse.
  function subirNuevaVersion(doc: SSODocument) {
    if (savingEdit) return;
    setEditTarget(null);
    setForm({
      title: doc.title, category: doc.category, description: doc.description ?? '',
      approved_from: '', expiry_date: '', checklist_item_id: doc.checklist_item_id ?? '',
    });
    setVig({ auto: false, nota: '' });
    quitarArchivoPrincipal();
    setExtras([]);
    setModalError('');
    setShowModal(true);
  }

  // Cancelar, ✕ o tocar fuera: no quedan el archivo elegido, los adicionales ni
  // los mensajes para la próxima vez (§17 ítem 89). Lo escrito se conserva.
  function cerrarSubida() {
    if (uploading) return;
    setShowModal(false);
    setFile(null);
    setExtras([]);
    setModalError('');
  }

  // D: limpia la selección y el value del input, para poder elegir otro archivo.
  function quitarArchivoPrincipal() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  // ── Upload ─────────────────────────────────────────────────────────────────

  async function handleUpload() {
    setModalError('');
    if (!form.title.trim())        { setModalError('El título es requerido.'); return; }
    if (!form.category)            { setModalError('La categoría es requerida.'); return; }
    if (!form.checklist_item_id)   { setModalError('Selecciona el ítem del checklist que resuelve este documento.'); return; }
    if (!file)                     { setModalError('Selecciona un archivo.'); return; }
    const fechasError = validarFechasDocumento(form.approved_from, form.expiry_date);
    if (fechasError)               { setModalError(fechasError); return; }
    for (const f of [file, ...extras.map(e => e.file)]) {
      const archivoError = validarArchivoSSO(f, form.checklist_item_id);
      if (archivoError)            { setModalError(archivoError); return; }
    }
    if (!companyId)                return;

    setUploading(true);
    try {
      let path: string;
      try {
        path = await subirAlBucket(companyId, file, form.checklist_item_id);
      } catch (upErr: any) {
        console.error('Error al subir el archivo a sso-documents:', upErr);
        setModalError(mensajeErrorSubida(upErr));
        return;
      }

      const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path);

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? null;

      // Detectar si ya existe una versión vigente del mismo documento
      // (mismo título + categoría) para encadenar la nueva versión. Se usa
      // order + limit(1) en vez de maybeSingle() porque datos previos a la
      // migración de versionado pueden tener más de una fila is_current=true
      // para el mismo título+categoría.
      const { data: existingRows } = await supabase
        .from('sso_documents')
        .select('id, version')
        .eq('company_id', companyId)
        .eq('category', form.category)
        .eq('title', form.title.trim())
        .eq('is_current', true)
        .order('version', { ascending: false })
        .limit(1);

      const existing = existingRows?.[0] ?? null;
      const version  = existing ? existing.version + 1 : 1;

      // Primero la fila nueva; la versión anterior se archiva solo si el insert
      // funcionó, y si falla se borra el archivo recién subido (§17 ítem 70).
      // file_name guarda el nombre ORIGINAL; solo la ruta usa el nombre seguro.
      const { data: nuevo, error: insErr } = await supabase.from('sso_documents').insert({
        company_id:         companyId,
        category:           form.category,
        title:              form.title.trim(),
        description:        form.description.trim() || null,
        file_url:           pub.publicUrl,
        file_name:          file.name,
        file_size_kb:       kbParaGuardar(file.size),
        uploaded_by:        userId,
        expiry_date:        form.expiry_date || null,
        approved_from:      form.approved_from || null,
        version,
        parent_document_id: existing?.id ?? null,
        is_current:         true,
        checklist_item_id:  form.checklist_item_id || null,
      }).select('id').single();

      if (insErr || !nuevo) {
        await supabase.storage.from('sso-documents').remove([path]);
        console.error('Error al guardar el documento SSO:', insErr);
        setModalError(MENSAJE_ERROR_SUBIDA);
        return;
      }

      const avisos: string[] = [];

      // Archivos del documento (H): el principal y los adicionales. Sin M11
      // solo queda el archivo de file_url, como antes.
      if (archivosDisponibles) {
        const { error: princErr } = await supabase.from('sso_document_files').insert({
          document_id: nuevo.id, company_id: companyId, file_path: path, file_name: file.name,
          file_size_kb: kbParaGuardar(file.size), kind: 'principal', uploaded_by: userId,
        });
        if (princErr) console.error('Error al registrar el archivo principal:', princErr);
        const fallidos = await agregarArchivos(nuevo.id, extras, userId, form.checklist_item_id);
        if (fallidos.length > 0) avisos.push(`El documento se guardó, pero no se pudo subir: ${fallidos.join(', ')}. Agrégalo desde Editar.`);
      }

      if (existing) {
        const { error: archiveErr } = await supabase
          .from('sso_documents')
          .update({ is_current: false })
          .eq('company_id', companyId)
          .eq('category', form.category)
          .eq('title', form.title.trim())
          .eq('is_current', true)
          .neq('id', nuevo.id);
        if (archiveErr) {
          console.error('Error al archivar la versión anterior:', archiveErr);
          avisos.push('El documento se guardó, pero la versión anterior sigue vigente.');
        }
      }

      logAudit({
        company_id:  companyId,
        action:      'created',
        entity_type: 'sso_document',
        entity_id:   nuevo.id,
        entity_name: form.title.trim(),
        details:     {
          checklist_item_id: form.checklist_item_id, version, file_name: file.name, path,
          approved_from: form.approved_from || null, expiry_date: form.expiry_date || null,
          archivos_adicionales: extras.map(e => ({ file_name: e.file.name, kind: e.kind })),
        },
      });

      setAviso(avisos.join(' '));
      setShowModal(false);
      setForm({ title: '', category: '', description: '', approved_from: '', expiry_date: '', checklist_item_id: '' });
      setVig({ auto: false, nota: '' });
      quitarArchivoPrincipal();
      setExtras([]);
      loadDocs();
    } catch (err) {
      console.error('Error inesperado al subir el documento SSO:', err);
      setModalError(MENSAJE_ERROR_SUBIDA);
    } finally {
      setUploading(false);
    }
  }

  // Sube los archivos adicionales y crea sus filas. Devuelve los nombres que fallaron.
  async function agregarArchivos(documentId: string, nuevos: ArchivoNuevo[], userId: string | null, itemId: string): Promise<string[]> {
    const fallidos: string[] = [];
    for (const n of nuevos) {
      let ruta = '';
      try {
        ruta = await subirAlBucket(companyId, n.file, itemId);
        const { error } = await supabase.from('sso_document_files').insert({
          document_id: documentId, company_id: companyId, file_path: ruta, file_name: n.file.name,
          file_size_kb: kbParaGuardar(n.file.size), kind: n.kind, uploaded_by: userId,
        });
        if (error) throw error;
      } catch (err) {
        console.error(`Error al agregar el archivo ${n.file.name}:`, err);
        if (ruta) await supabase.storage.from('sso-documents').remove([ruta]);
        fallidos.push(n.file.name);
      }
    }
    return fallidos;
  }

  // ── Edit (B2) ──────────────────────────────────────────────────────────────

  function openEdit(doc: SSODocument) {
    setEditTarget(doc);
    setEditError('');
    setEditVig({ auto: false, nota: '' });
    setEditExtras([]);
    setEditQuitar([]);
    quitarPrincipalEdicion();
    setEditForm({
      title:             doc.title,
      category:          doc.category,
      checklist_item_id: doc.checklist_item_id ?? '',
      approved_from:     doc.approved_from ?? '',
      expiry_date:       doc.expiry_date ?? '',
    });
  }

  function quitarPrincipalEdicion() {
    setEditPrincipal(null);
    if (editFileRef.current) editFileRef.current.value = '';
  }

  // Título y categoría encadenan las versiones (mismo título + categoría): si
  // cambian, se cambian en todas las versiones. El ítem, las fechas, el archivo
  // principal (5d, C) y los adicionales, solo en la vigente (decisión de JC, 19/10/2026).
  async function handleSaveEdit() {
    if (!editTarget) return;
    setEditError('');
    const title = editForm.title.trim();
    const item  = editForm.checklist_item_id;
    if (!title)                      { setEditError('El título es requerido.'); return; }
    if (!editForm.category)          { setEditError('La categoría es requerida.'); return; }
    if (!item)                       { setEditError('Selecciona el ítem del checklist que resuelve este documento.'); return; }
    const fechasError = validarFechasDocumento(editForm.approved_from, editForm.expiry_date);
    if (fechasError)                 { setEditError(fechasError); return; }
    for (const f of [...(editPrincipal ? [editPrincipal] : []), ...editExtras.map(n => n.file)]) {
      const archivoError = validarArchivoSSO(f, item);
      if (archivoError)              { setEditError(archivoError); return; }
    }
    // Si cambia el ítem, un plano de AutoCAD que ya estaba no puede quedar en un ítem que no lo acepta (5d, G).
    if (!itemAceptaAutocad(item)) {
      const quedan = [
        ...(editPrincipal ? [] : [editTarget.file_name]),
        ...extrasDe(editTarget.id).filter(a => !editQuitar.includes(a.id)).map(a => a.file_name),
      ];
      if (quedan.some(esArchivoAutocad)) { setEditError(MENSAJE_AUTOCAD_SOLO_ITEM); return; }
    }

    const antes = {
      title: editTarget.title, category: editTarget.category, checklist_item_id: editTarget.checklist_item_id,
      approved_from: editTarget.approved_from, expiry_date: editTarget.expiry_date,
    };
    const despues = {
      title, category: editForm.category, checklist_item_id: editForm.checklist_item_id,
      approved_from: editForm.approved_from || null, expiry_date: editForm.expiry_date || null,
    };
    const cambios = cambiosDocumento(antes, despues);
    const quitar = extrasDe(editTarget.id).filter(a => editQuitar.includes(a.id));
    const nuevoPrincipal = editPrincipal;
    if (Object.keys(cambios).length === 0 && editExtras.length === 0 && quitar.length === 0 && !nuevoPrincipal) { setEditTarget(null); return; }

    setSavingEdit(true);
    // Ruta del principal nuevo ya subido y si la fila ya apunta a él: si algo
    // falla antes de actualizar la fila, el archivo nuevo se borra y no cambia nada.
    let rutaNueva: string | null = null;
    let filaConArchivoNuevo = false;
    try {
      const cambiaGrupo = 'title' in cambios || 'category' in cambios;

      if (cambiaGrupo) {
        // No juntar dos documentos distintos en una misma cadena de versiones.
        const { data: choque } = await supabase
          .from('sso_documents')
          .select('id')
          .eq('company_id', companyId)
          .eq('category', despues.category)
          .eq('title', title)
          .limit(1);
        if (choque && choque.length > 0) {
          setEditError('Ya existe otro documento con ese título y categoría. Usa un título distinto.');
          return;
        }
      }

      // C2: primero se sube el archivo nuevo; si la subida falla no se cambia nada.
      if (nuevoPrincipal) {
        try {
          rutaNueva = await subirAlBucket(companyId, nuevoPrincipal, item);
        } catch (upErr: any) {
          console.error('Error al subir el archivo que reemplaza al principal:', upErr);
          setEditError(mensajeErrorSubida(upErr));
          return;
        }
      }

      if (cambiaGrupo) {
        const { error: grupoErr } = await supabase
          .from('sso_documents')
          .update({ title, category: despues.category, updated_at: new Date().toISOString() })
          .eq('company_id', companyId)
          .eq('category', editTarget.category)
          .eq('title', editTarget.title);
        if (grupoErr) {
          console.error('Error al guardar el título o la categoría:', grupoErr);
          setEditError('No se pudo guardar el título o la categoría. Intenta de nuevo.');
          return;
        }
      }

      if (Object.keys(cambios).length > 0 || rutaNueva) {
        const { data: actualizada, error } = await supabase
          .from('sso_documents')
          .update({
            checklist_item_id: despues.checklist_item_id,
            approved_from:     despues.approved_from,
            expiry_date:       despues.expiry_date,
            updated_at:        new Date().toISOString(),
            ...(rutaNueva && nuevoPrincipal ? {
              file_url:     urlArchivo(rutaNueva),
              file_name:    nuevoPrincipal.name,
              file_size_kb: kbParaGuardar(nuevoPrincipal.size),
            } : {}),
          })
          .eq('id', editTarget.id)
          .select('id');
        if (error || !actualizada || actualizada.length === 0) {
          console.error('Error al guardar los cambios del documento:', error);
          setEditError('No se pudieron guardar los cambios (o no tienes permiso para editar).');
          loadDocs();
          return;
        }
        filaConArchivoNuevo = !!rutaNueva;
      }

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? null;

      // C2: la fila 'principal' de sso_document_files y después el archivo anterior.
      const avisos: string[] = [];
      let reemplazo: Record<string, unknown> | null = null;
      if (rutaNueva && nuevoPrincipal) {
        const filaPrincipal = (archivosPorDoc[editTarget.id] ?? []).find(a => a.kind === 'principal') ?? null;
        const rutaAnterior = filaPrincipal?.file_path ?? rutaStorageDesdeUrl(editTarget.file_url);
        let filaArchivosOk = true;
        if (archivosDisponibles) {
          const datos = {
            file_path: rutaNueva, file_name: nuevoPrincipal.name,
            file_size_kb: kbParaGuardar(nuevoPrincipal.size), uploaded_by: userId,
          };
          const { data: filas, error: filaErr } = filaPrincipal
            ? await supabase.from('sso_document_files').update(datos).eq('id', filaPrincipal.id).select('id')
            : await supabase.from('sso_document_files')
                .insert({ ...datos, document_id: editTarget.id, company_id: companyId, kind: 'principal' }).select('id');
          filaArchivosOk = !filaErr && !!filas && filas.length > 0;
          if (!filaArchivosOk) console.error('Error al actualizar la fila del archivo principal:', filaErr ?? 'sin filas');
        }
        const anterior = filaArchivosOk && rutaAnterior
          ? await borrarArchivoAnterior(editTarget, rutaAnterior)
          : 'error';
        if (anterior === 'error') avisos.push(`Se reemplazó el archivo de "${title}", pero el anterior (${editTarget.file_name}) no se pudo borrar del almacenamiento.`);
        reemplazo = {
          antes: editTarget.file_name, despues: nuevoPrincipal.name,
          ruta_anterior: rutaAnterior, ruta_nueva: rutaNueva, archivo_anterior: anterior,
        };
      }

      // Archivos adicionales: primero se quitan los marcados, después se suben los nuevos.
      const noQuitados: string[] = [];
      for (const a of quitar) {
        const { data: removed, error: rmErr } = await supabase.storage.from('sso-documents').remove([a.file_path]);
        if (rmErr || !removed || removed.length === 0) {
          console.error(`Error al quitar ${a.file_name} del almacenamiento:`, rmErr ?? 'no encontrado o sin permiso');
          noQuitados.push(a.file_name);
          continue;
        }
        const { error: delErr } = await supabase.from('sso_document_files').delete().eq('id', a.id);
        if (delErr) { console.error(`Error al quitar la fila de ${a.file_name}:`, delErr); noQuitados.push(a.file_name); }
      }
      const fallidos = await agregarArchivos(editTarget.id, editExtras, userId, item);

      // audit_logs "Editado" (C3): con el nombre del archivo anterior y del nuevo.
      logAudit({
        company_id:  companyId,
        action:      'updated',
        entity_type: 'sso_document',
        entity_id:   editTarget.id,
        entity_name: title,
        details:     {
          cambios, version: editTarget.version, todas_las_versiones: cambiaGrupo,
          ...(reemplazo ? { archivo_principal: reemplazo } : {}),
          archivos_agregados: editExtras.filter(n => !fallidos.includes(n.file.name)).map(n => ({ file_name: n.file.name, kind: n.kind })),
          archivos_quitados:  quitar.filter(a => !noQuitados.includes(a.file_name)).map(a => ({ file_name: a.file_name, kind: a.kind })),
        },
      });

      loadDocs();
      if (avisos.length > 0) setAviso(avisos.join(' '));
      if (fallidos.length > 0 || noQuitados.length > 0) {
        // El modal queda abierto con lo que falta; lo que sí se guardó ya está en la lista.
        setEditExtras(prev => prev.filter(n => fallidos.includes(n.file.name)));
        setEditQuitar([]);
        quitarPrincipalEdicion();
        setEditError([
          fallidos.length > 0 ? `No se pudo subir: ${fallidos.join(', ')}.` : '',
          noQuitados.length > 0 ? `No se pudo quitar: ${noQuitados.join(', ')}.` : '',
          'Intenta de nuevo.',
        ].filter(Boolean).join(' '));
        setEditTarget({
          ...editTarget, title, category: despues.category as SSODocCategory, checklist_item_id: despues.checklist_item_id,
          approved_from: despues.approved_from, expiry_date: despues.expiry_date,
          ...(rutaNueva && nuevoPrincipal ? {
            file_url: urlArchivo(rutaNueva), file_name: nuevoPrincipal.name, file_size_kb: kbParaGuardar(nuevoPrincipal.size),
          } : {}),
        });
        return;
      }
      setEditTarget(null);
    } catch (err) {
      console.error('Error inesperado al editar el documento SSO:', err);
      setEditError('No se pudieron guardar los cambios. Intenta de nuevo.');
    } finally {
      // El archivo nuevo quedó subido pero la fila no lo usa: se borra (no cambia nada).
      if (rutaNueva && !filaConArchivoNuevo) {
        const { error: rmErr } = await supabase.storage.from('sso-documents').remove([rutaNueva]);
        if (rmErr) console.error('No se pudo borrar el archivo subido que no se usó:', rmErr);
      }
      setSavingEdit(false);
    }
  }

  // Borra del bucket el principal reemplazado, solo si ninguna otra fila lo usa
  // (otra versión u otro documento): en ese caso se conserva a propósito.
  async function borrarArchivoAnterior(doc: SSODocument, ruta: string): Promise<'borrado' | 'en_uso' | 'error'> {
    const { data: otrosDocs, error: e1 } = await supabase
      .from('sso_documents').select('id')
      .eq('company_id', companyId).eq('file_url', doc.file_url).neq('id', doc.id).limit(1);
    let otrosArchivos: { id: string }[] | null = [];
    let e2: unknown = null;
    if (archivosDisponibles) {
      const r = await supabase
        .from('sso_document_files').select('id')
        .eq('company_id', companyId).eq('file_path', ruta).neq('document_id', doc.id).limit(1);
      otrosArchivos = r.data;
      e2 = r.error;
    }
    if (e1 || e2) { console.error('No se pudo revisar si el archivo anterior se usa en otra fila:', e1 ?? e2); return 'error'; }
    if ((otrosDocs ?? []).length > 0 || (otrosArchivos ?? []).length > 0) {
      console.warn('El archivo anterior lo usa otra fila; se conserva:', ruta);
      return 'en_uso';
    }
    const { data: removed, error: rmErr } = await supabase.storage.from('sso-documents').remove([ruta]);
    if (rmErr || !removed || removed.length === 0) {
      console.error('Error al borrar el archivo anterior:', rmErr ?? 'no encontrado o sin permiso');
      return 'error';
    }
    return 'borrado';
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  // E2 (bloque 6, §17 ítem 105): al abrir "Eliminar" se calcula qué se borra:
  // todas las versiones (mismo título y categoría) con sus archivos.
  function abrirEliminar(doc: SSODocument) {
    setDeleteTarget(doc);
    setDeleteStep(1);
    setDeleteError('');
    setDeletePlan(null);
    cargarPlanBorrado(doc);
  }

  async function cargarPlanBorrado(doc: SSODocument) {
    const { data: versiones, error: vErr } = await supabase
      .from('sso_documents')
      .select('id, version, file_url, is_current')
      .eq('company_id', companyId)
      .eq('category', doc.category)
      .eq('title', doc.title);
    if (vErr || !versiones || versiones.length === 0) {
      console.error('Error al leer las versiones del documento:', vErr);
      setDeleteError('No se pudieron leer las versiones del documento. Intenta de nuevo.');
      return;
    }
    let archivos: { document_id: string; file_path: string }[] = [];
    if (archivosDisponibles) {
      const { data, error } = await supabase
        .from('sso_document_files')
        .select('document_id, file_path')
        .in('document_id', versiones.map(v => v.id));
      if (error) {
        console.error('Error al leer los archivos de las versiones:', error);
        setDeleteError('No se pudieron leer los archivos del documento. Intenta de nuevo.');
        return;
      }
      archivos = data ?? [];
    }
    setDeletePlan(planBorradoDocumento(versiones as VersionDocumento[], archivos));
  }

  async function handleDelete() {
    if (!deleteTarget || !deletePlan) return;
    setDeleting(true);
    setDeleteError('');
    const fallas: string[] = [];
    try {
      // Las archivadas primero y la vigente al final (planBorradoDocumento): si una
      // falla, se detiene antes de la vigente y el documento sigue en la lista.
      for (const paso of deletePlan) {
        // file_url viene de getPublicUrl() (codificada): rutaStorageDesdeUrl la
        // decodifica para que remove() encuentre el objeto. Las filas de
        // sso_document_files caen por CASCADE al borrar la versión.
        if (paso.rutas.length > 0) {
          const { data: removed, error: rmErr } = await supabase.storage.from('sso-documents').remove(paso.rutas);
          // remove() no da error si no encontró el archivo (o no hay permiso):
          // se compara lo borrado con lo pedido y, si falta algo, la fila se conserva.
          const faltan = rmErr ? paso.rutas : rutasNoBorradas(paso, removed);
          if (faltan.length > 0) {
            console.error(`No se borraron archivos de la versión ${paso.version}:`, rmErr ?? faltan);
            fallas.push(`v${paso.version}: no se pudo borrar ${faltan.length === paso.rutas.length ? 'su archivo' : `${faltan.length} de sus archivos`} del almacenamiento (no se encontró o no tienes permiso); su registro se conserva`);
            break;
          }
        }
        const { data: borradas, error: delErr } = await supabase
          .from('sso_documents')
          .delete()
          .eq('id', paso.id)
          .select('id');
        if (delErr || !borradas || borradas.length === 0) {
          console.error(`Error al borrar la fila de la versión ${paso.version}:`, delErr);
          fallas.push(`v${paso.version}: el archivo se borró, pero no el registro (sin permiso para borrarlo)`);
          break;
        }
        logAudit({
          company_id:  companyId,
          action:      'deleted',
          entity_type: 'sso_document',
          entity_id:   paso.id,
          entity_name: deleteTarget.title,
          details:     {
            checklist_item_id: deleteTarget.checklist_item_id,
            version:           paso.version,
            vigente:           paso.is_current,
            versiones_del_documento: deletePlan.length,
            rutas_borradas:    paso.rutas,
          },
        });
      }

      loadDocs();
      if (fallas.length > 0) {
        setDeleteError(`El documento no se eliminó por completo. ${fallas.join('. ')}. Intenta de nuevo.`);
        cargarPlanBorrado(deleteTarget);
        return;
      }
      setDeleteTarget(null);
      setDeleteStep(1);
    } catch (err) {
      console.error('Error inesperado al eliminar el documento SSO:', err);
      setDeleteError('No se pudo eliminar el documento. Intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  }

  // ── Version history ──────────────────────────────────────────────────────────

  async function openHistory(doc: SSODocument) {
    setHistoryTarget(doc);
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from('sso_documents')
      .select('*')
      .eq('company_id', companyId)
      .eq('category', doc.category)
      .eq('title', doc.title)
      .order('version', { ascending: false });
    if (error) console.error('Error al cargar historial de versiones:', error.message);
    setHistoryDocs((data ?? []) as SSODocument[]);
    setHistoryLoading(false);
  }

  function extrasDe(docId: string) {
    return (archivosPorDoc[docId] ?? []).filter(a => a.kind !== 'principal');
  }

  const avisoSubida  = avisoVigencia(form.approved_from, form.expiry_date);
  // Mismo título y categoría que uno vigente = nueva versión (lo decide handleUpload; esto solo lo anuncia).
  const vigenteIgual = form.title.trim() && form.category
    ? docs.find(d => d.title === form.title.trim() && d.category === form.category) : undefined;
  const versionSiguiente = vigenteIgual ? vigenteIgual.version + 1 : null;
  const avisoEdicion = avisoVigencia(editForm.approved_from, editForm.expiry_date);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Gestión Documental SSO</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {docs.length} documento{docs.length !== 1 ? 's' : ''} · Reglamentos, actas, permisos y más
          </p>
        </div>
        {canUpload && (
          <button onClick={() => abrirSubida(filterItem || undefined)} style={primaryBtn}>
            + Subir documento
          </button>
        )}
      </div>

      {aviso && (
        <div style={{ ...avisoBox, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span>{aviso}</span>
          <button onClick={() => setAviso('')} style={{ ...closeBtn, fontSize: '0.9rem' }}>✕</button>
        </div>
      )}

      {/* Deep-link banner */}
      {isDeepLinkFilter && filterItem && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          padding: '10px 16px', background: 'rgba(29,111,163,0.1)', border: '1px solid rgba(29,111,163,0.3)', borderRadius: 10,
        }}>
          <span style={{ color: '#93c5fd', fontSize: '0.84rem' }}>
            Mostrando documentos de: <strong>{CHECKLIST_ITEM_FILTER_OPTIONS.find(o => o.id === filterItem)?.label ?? filterItem}</strong>
          </span>
          <button
            onClick={() => { setFilterItem(''); setIsDeepLinkFilter(false); }}
            style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}
          >
            Ver todos los documentos
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...filterInp, flex: '1 1 200px', minWidth: 160 }}
          placeholder="Buscar por título…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={filterInp} value={filterCat} onChange={e => setFilterCat(e.target.value as SSODocCategory | '')}>
          <option value="">Categoría: todas</option>
          {SSO_DOC_CATEGORIES.map(c =>
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          )}
        </select>
        <select style={{ ...filterInp, maxWidth: '100%' }} value={filterItem} onChange={e => { setFilterItem(e.target.value); setIsDeepLinkFilter(false); }}>
          <option value="">Ítem: todos</option>
          {CHECKLIST_ITEM_FILTER_OPTIONS.map(opt =>
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          )}
        </select>
        {(search || filterCat || filterItem) && (
          <button onClick={() => { setSearch(''); setFilterCat(''); setFilterItem(''); setIsDeepLinkFilter(false); }}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.82rem' }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* GA-07 por mes (5d, D) */}
      {filterItem === 'GA-07' && !loading && (
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: '12px 16px' }}>
          <MesesGA07
            informes={informesGA07}
            onAbrir={inf => void abrirArchivo('sso-documents', inf.file_url)}
            onSubir={canUpload ? fecha => abrirSubida('GA-07', fecha) : undefined}
          />
        </div>
      )}

      {/* Document list */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {docs.length === 0 ? 'Aún no hay documentos subidos.' : 'Sin resultados para los filtros aplicados.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Documento', 'Categoría', 'Aprobado desde', 'Vigente hasta', 'Cargado en StorSafe el', 'Tamaño', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(doc => {
                  const cat   = CATEGORY_COLORS[doc.category];
                  const exp   = expiryStatus(doc.expiry_date);
                  const ext   = fileExtension(doc.file_name);
                  return (
                    <tr key={doc.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                      {/* Title + filename */}
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FileIcon ext={ext} />
                          <div>
                            <div style={{ color: '#e2e8f0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                              {doc.title}
                              {doc.version > 1 && (
                                <span style={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 700, background: 'rgba(100,116,139,0.15)', borderRadius: 4, padding: '1px 6px' }}>
                                  v{doc.version}
                                </span>
                              )}
                            </div>
                            <div style={{ color: '#475569', fontSize: '0.74rem' }}>{doc.file_name}</div>
                            {extrasDe(doc.id).map(a => (
                              <div key={a.id} style={{ fontSize: '0.72rem' }}>
                                <span style={{ color: '#64748b' }}>{SSO_KIND_LABELS[a.kind]}: </span>
                                <EnlaceArchivo bucket="sso-documents" valor={a.file_path} style={{ color: '#60a5fa', textDecoration: 'none' }}>
                                  {a.file_name}
                                </EnlaceArchivo>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>

                      {/* Category badge */}
                      <td style={td}>
                        <span style={{ background: cat.bg, color: cat.color, border: `1px solid ${cat.border}`, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {CATEGORY_LABELS[doc.category]}
                        </span>
                      </td>

                      {/* Aprobado desde (manual) */}
                      <td style={{ ...td, color: '#94a3b8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {doc.approved_from ? formatDate(doc.approved_from) : <span style={{ color: '#334155' }}>—</span>}
                      </td>

                      {/* Vigente hasta (manual, opcional): "Vencido" sale solo de esta fecha */}
                      <td style={td}>
                        {doc.expiry_date ? (
                          <span style={{
                            color: exp === 'expired' ? '#ef4444' : exp === 'warning' ? '#f59e0b' : '#94a3b8',
                            fontWeight: exp === 'expired' || exp === 'warning' ? 700 : 400,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                          }}>
                            {exp === 'expired' && '⚠ Vencido · '}
                            {formatDate(doc.expiry_date)}
                          </span>
                        ) : <span style={{ color: '#475569', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Sin vencimiento</span>}
                      </td>

                      {/* Cargado en StorSafe el (automático) */}
                      <td style={{ ...td, color: '#64748b', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {new Date(doc.created_at).toLocaleDateString('es-EC')}
                      </td>

                      {/* Size */}
                      <td style={{ ...td, color: '#64748b', fontSize: '0.8rem' }}>
                        {formatSize(doc.file_size_kb)}
                      </td>

                      {/* Actions */}
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <EnlaceArchivo
                            bucket="sso-documents"
                            valor={doc.file_url}
                            style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', textDecoration: 'none', display: 'inline-block', lineHeight: '1.4' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                          >
                            Ver / Descargar
                          </EnlaceArchivo>
                          {canUpload && (
                            <button
                              onClick={() => openEdit(doc)}
                              style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#fbbf24'; e.currentTarget.style.color = '#fbbf24'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              Editar
                            </button>
                          )}
                          {doc.version > 1 && (
                            <button
                              onClick={() => openHistory(doc)}
                              style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#60a5fa'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              Ver historial de versiones
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => abrirEliminar(doc)}
                              style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filtered.length} de {docs.length} documento{docs.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* ── Upload Modal ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && cerrarSubida()}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Subir documento SSO</h2>
              <button onClick={cerrarSubida} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              <Field label="Título *">
                <input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Reglamento SSO 2025" />
              </Field>

              <Field label="Categoría *">
                <BuscadorOpciones
                  ariaLabel="Categoría"
                  opciones={OPCIONES_CATEGORIA}
                  value={form.category}
                  onChange={v => cambiarForm({ category: v as SSODocCategory })}
                  placeholder="Escribe para buscar (ej. registro)"
                  inputStyle={sel}
                  disabled={uploading}
                />
              </Field>
              {versionSiguiente && (
                <span style={{ ...notaSugerencia, marginTop: -6 }}>
                  Ya hay un documento vigente con este título y categoría: se guardará como versión {versionSiguiente} y el actual pasará al historial.
                </span>
              )}

              <Field label="Descripción">
                <textarea style={{ ...inp, minHeight: 72, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción breve del documento (opcional)" />
              </Field>

              <Field label="¿Qué ítem del checklist resuelve este documento? *">
                <select style={sel} value={form.checklist_item_id} onChange={e => cambiarForm({ checklist_item_id: e.target.value })}>
                  <option value="">— Seleccionar —</option>
                  {ordenarOpcionesItem(CHECKLIST_ITEM_UPLOAD_OPTIONS, form.category).map(opt =>
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  )}
                </select>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <Field label="Aprobado desde">
                  <input style={inp} type="date" value={form.approved_from} onChange={e => cambiarForm({ approved_from: e.target.value })} />
                </Field>
                <Field label="Vigente hasta (vacío = sin vencimiento)">
                  <input style={inp} type="date" value={form.expiry_date} min={form.approved_from || undefined} onChange={e => cambiarForm({ expiry_date: e.target.value })} />
                </Field>
              </div>
              {vig.nota && <span style={notaSugerencia}>{vig.nota}</span>}
              {avisoSubida && <span style={avisoBox}>{avisoSubida}</span>}
              <span style={{ color: '#64748b', fontSize: '0.74rem', marginTop: -6 }}>
                Las fechas se escriben a mano, tal como figuran en el documento. "Cargado en StorSafe el" se registra solo.
              </span>

              <Field label={archivosDisponibles ? 'Archivo principal *' : 'Archivo *'}>
                <input
                  ref={fileRef}
                  style={{ ...inp, padding: '7px 12px', cursor: 'pointer' }}
                  type="file"
                  accept={acceptSSO(form.checklist_item_id)}
                  onChange={e => {
                    const f = e.target.files?.[0] ?? null;
                    const err = f ? validarArchivoSSO(f, form.checklist_item_id) : null;
                    if (err) { setModalError(err); quitarArchivoPrincipal(); return; }
                    setModalError('');
                    setFile(f);
                  }}
                />
                {file && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ color: '#64748b', fontSize: '0.76rem' }}>{file.name} — {formatSize(kbParaGuardar(file.size))}</span>
                    <button type="button" onClick={quitarArchivoPrincipal} disabled={uploading} style={linkBtn}>Quitar archivo</button>
                  </div>
                )}
                {itemAceptaAutocad(form.checklist_item_id) && (
                  <span style={{ color: '#64748b', fontSize: '0.74rem' }}>En este ítem también se aceptan planos de AutoCAD (.dwg, .dxf).</span>
                )}
              </Field>

              {archivosDisponibles && (
                <ArchivosNuevos
                  lista={extras}
                  setLista={setExtras}
                  onError={setModalError}
                  disabled={uploading}
                  itemId={form.checklist_item_id}
                />
              )}

              {modalError && <div role="alert" style={errorBox}>{modalError}</div>}
            </div>
            <div style={modalFooter}>
              <button onClick={cerrarSubida} style={cancelBtn}>Cancelar</button>
              <button onClick={handleUpload} disabled={uploading} style={{ ...saveBtn, opacity: uploading ? 0.7 : 1 }}>
                {uploading ? 'Subiendo…' : 'Subir documento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal (B2) ───────────────────────────────────────────────────── */}
      {editTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !savingEdit && setEditTarget(null)}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Editar documento</h2>
              <button onClick={() => !savingEdit && setEditTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
              <Field label={archivosDisponibles ? 'Archivo principal' : 'Archivo'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '0.78rem' }}>
                  <EnlaceArchivo bucket="sso-documents" valor={editTarget.file_url}
                    style={{ color: editPrincipal ? '#475569' : '#60a5fa', textDecoration: editPrincipal ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>
                    {editTarget.file_name}
                  </EnlaceArchivo>
                  <span style={{ color: '#64748b' }}>{formatSize(editTarget.file_size_kb)} · cargado en StorSafe el {new Date(editTarget.created_at).toLocaleDateString('es-EC')}</span>
                </div>
                {editPrincipal && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.76rem', overflowWrap: 'anywhere' }}>
                      Nuevo: {editPrincipal.name} — {formatSize(kbParaGuardar(editPrincipal.size))}
                    </span>
                    <button type="button" onClick={quitarPrincipalEdicion} disabled={savingEdit} style={linkBtn}>Quitar archivo</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!editPrincipal && (
                    <button type="button" disabled={savingEdit} style={{ ...linkBtn, padding: '6px 12px' }}
                      onClick={() => editFileRef.current?.click()}>
                      Reemplazar archivo
                    </button>
                  )}
                  <button type="button" disabled={savingEdit} style={{ ...linkBtn, padding: '6px 12px' }}
                    onClick={() => subirNuevaVersion(editTarget)}>
                    Subir nueva versión
                  </button>
                </div>
                <input
                  ref={editFileRef}
                  type="file"
                  accept={acceptSSO(editForm.checklist_item_id)}
                  tabIndex={-1}
                  aria-hidden="true"
                  style={archivoOculto}
                  onChange={e => {
                    const f = e.target.files?.[0] ?? null;
                    const err = f ? validarArchivoSSO(f, editForm.checklist_item_id) : null;
                    if (err) { setEditError(err); quitarPrincipalEdicion(); return; }
                    setEditError('');
                    setEditPrincipal(f);
                  }}
                />
                <span style={{ color: '#64748b', fontSize: '0.74rem' }}>
                  "Reemplazar archivo" cambia el archivo de esta versión y borra el anterior; "Subir nueva versión" guarda el anterior en el historial.
                </span>
              </Field>
              <Field label="Título *">
                <input style={inp} value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
              </Field>
              <Field label="Categoría *">
                <BuscadorOpciones
                  ariaLabel="Categoría"
                  opciones={OPCIONES_CATEGORIA}
                  value={editForm.category}
                  onChange={v => setEditForm(f => ({ ...f, category: v as SSODocCategory }))}
                  placeholder="Escribe para buscar (ej. registro)"
                  inputStyle={sel}
                  disabled={savingEdit}
                />
              </Field>
              {editTarget.version > 1 && (
                <span style={{ color: '#64748b', fontSize: '0.74rem', marginTop: -6 }}>
                  El título y la categoría se cambian en todas las versiones; el ítem, las fechas y los archivos, solo en la vigente (v{editTarget.version}).
                </span>
              )}
              <Field label="¿Qué ítem del checklist resuelve este documento? *">
                <select style={sel} value={editForm.checklist_item_id} onChange={e => cambiarEditForm({ checklist_item_id: e.target.value })}>
                  <option value="">— Seleccionar —</option>
                  {ordenarOpcionesItem(opcionesItemEdicion(editForm.checklist_item_id), editForm.category).map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                </select>
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <Field label="Aprobado desde">
                  <input style={inp} type="date" value={editForm.approved_from} onChange={e => cambiarEditForm({ approved_from: e.target.value })} />
                </Field>
                <Field label="Vigente hasta (vacío = sin vencimiento)">
                  <input style={inp} type="date" value={editForm.expiry_date} min={editForm.approved_from || undefined} onChange={e => cambiarEditForm({ expiry_date: e.target.value })} />
                </Field>
              </div>
              {editVig.nota && <span style={notaSugerencia}>{editVig.nota}</span>}
              {avisoEdicion && <span style={avisoBox}>{avisoEdicion}</span>}

              {archivosDisponibles && (
                <>
                  {extrasDe(editTarget.id).length > 0 && (
                    <Field label="Archivos adicionales">
                      {extrasDe(editTarget.id).map(a => {
                        const marcado = editQuitar.includes(a.id);
                        return (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '0.78rem' }}>
                            <span style={{ color: '#64748b' }}>{SSO_KIND_LABELS[a.kind]}:</span>
                            <EnlaceArchivo bucket="sso-documents" valor={a.file_path}
                              style={{ color: marcado ? '#475569' : '#60a5fa', textDecoration: marcado ? 'line-through' : 'none' }}>
                              {a.file_name}
                            </EnlaceArchivo>
                            {canDelete && (
                              <button type="button" disabled={savingEdit} style={linkBtn}
                                onClick={() => setEditQuitar(q => marcado ? q.filter(id => id !== a.id) : [...q, a.id])}>
                                {marcado ? 'Deshacer' : 'Quitar'}
                              </button>
                            )}
                            {marcado && <span style={{ color: '#f59e0b' }}>Se quitará al guardar</span>}
                          </div>
                        );
                      })}
                    </Field>
                  )}
                  <ArchivosNuevos lista={editExtras} setLista={setEditExtras} onError={setEditError} disabled={savingEdit} itemId={editForm.checklist_item_id} />
                </>
              )}

              {editError && <div role="alert" style={errorBox}>{editError}</div>}
            </div>
            <div style={modalFooter}>
              <button onClick={() => !savingEdit && setEditTarget(null)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSaveEdit} disabled={savingEdit} style={{ ...saveBtn, opacity: savingEdit ? 0.7 : 1 }}>
                {savingEdit ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 440 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Eliminar documento</h2>
              <button onClick={() => !deleting && setDeleteTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {deleteStep === 1 ? (
                <>
                  <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 16px' }}>
                    ¿Eliminar <strong style={{ color: '#f1f5f9' }}>{deleteTarget.title}</strong>?
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px' }}>
                    Esto eliminará el archivo del almacenamiento y el registro de la base de datos. No se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteTarget(null)} style={cancelBtn}>Cancelar</button>
                    <button onClick={() => setDeleteStep(2)} style={{ ...saveBtn, background: '#dc2626' }}>Continuar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#fca5a5', fontSize: '0.88rem', fontWeight: 600, margin: '0 0 20px' }}>
                    {deletePlan
                      ? (() => {
                          const r = resumenBorrado(deletePlan);
                          return `⚠ Se borrarán ${r.versiones === 1 ? 'la única versión' : `las ${r.versiones} versiones (la vigente y ${r.versiones - 1} del historial)`} y ${r.archivos === 1 ? '1 archivo' : `${r.archivos} archivos`} del almacenamiento. Esta acción no se puede deshacer.`;
                        })()
                      : 'Calculando las versiones y los archivos que se borrarán…'}
                  </p>
                  {deleteError && <div role="alert" style={{ ...errorBox, marginBottom: 16 }}>{deleteError}</div>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteStep(1)} style={cancelBtn}>Atrás</button>
                    <button onClick={handleDelete} disabled={deleting || !deletePlan} style={{ ...saveBtn, background: '#dc2626', opacity: deleting || !deletePlan ? 0.7 : 1 }}>
                      {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Version History Modal ─────────────────────────────────────────────── */}
      {historyTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && setHistoryTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 560 }}>
            <div style={modalHeader}>
              <div>
                <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Historial de versiones</h2>
                <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '4px 0 0' }}>{historyTarget.title}</p>
              </div>
              <button onClick={() => setHistoryTarget(null)} style={closeBtn}>✕</button>
            </div>

            <div style={{ padding: '16px 24px 24px', maxHeight: 420, overflowY: 'auto' }}>
              {historyLoading ? (
                <p style={{ color: '#475569', fontSize: '0.88rem', textAlign: 'center', padding: 24 }}>Cargando…</p>
              ) : historyDocs.length === 0 ? (
                <p style={{ color: '#475569', fontSize: '0.88rem', textAlign: 'center', padding: 24 }}>Sin versiones registradas.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {historyDocs.map(d => (
                    <div key={d.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                      padding: '10px 14px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.86rem' }}>v{d.version}</span>
                          {d.is_current && (
                            <span style={{ color: '#2FBF71', background: 'rgba(47,191,113,0.12)', border: '1px solid rgba(47,191,113,0.3)', borderRadius: 6, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                              Actual
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '0.76rem', marginTop: 2 }}>
                          {d.file_name} · cargado el {new Date(d.created_at).toLocaleDateString('es-EC')}{d.approved_from ? ` · aprobado desde ${formatDate(d.approved_from)}` : ''}
                        </div>
                      </div>
                      <EnlaceArchivo
                        bucket="sso-documents"
                        valor={d.file_url}
                        descargar={d.file_name}
                        style={{ padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                      >
                        Descargar
                      </EnlaceArchivo>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// Archivos adicionales elegidos y todavía no subidos (H), cada uno con su tipo.
function ArchivosNuevos({ lista, setLista, onError, disabled, itemId }: {
  lista: ArchivoNuevo[];
  setLista: React.Dispatch<React.SetStateAction<ArchivoNuevo[]>>;
  onError: (m: string) => void;
  disabled: boolean;
  itemId: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <Field label="Agregar archivos (resolución, QR, lista de asistencia, registro fotográfico, anexos)">
      {lista.map(n => (
        <div key={n.key} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select style={{ ...sel, width: 'auto', padding: '5px 8px', fontSize: '0.78rem' }} value={n.kind} disabled={disabled}
            aria-label={`Tipo de ${n.file.name}`}
            onChange={e => setLista(l => l.map(x => x.key === n.key ? { ...x, kind: e.target.value as ArchivoNuevo['kind'] } : x))}>
            {SSO_KINDS_ADICIONALES.map(k => <option key={k} value={k}>{SSO_KIND_LABELS[k]}</option>)}
          </select>
          <span style={{ color: '#94a3b8', fontSize: '0.76rem' }}>{n.file.name} — {formatSize(kbParaGuardar(n.file.size))}</span>
          <button type="button" style={linkBtn} disabled={disabled} onClick={() => setLista(l => l.filter(x => x.key !== n.key))}>Quitar archivo</button>
        </div>
      ))}
      <input
        ref={ref}
        style={{ ...inp, padding: '7px 12px', cursor: 'pointer' }}
        type="file"
        multiple
        accept={acceptSSO(itemId)}
        disabled={disabled}
        onChange={e => {
          const elegidos = Array.from(e.target.files ?? []);
          const malo = elegidos.map(f => validarArchivoSSO(f, itemId)).find(Boolean);
          if (malo) onError(malo);
          else onError('');
          const validos = elegidos.filter(f => !validarArchivoSSO(f, itemId));
          setLista(l => [...l, ...validos.map(file => ({ key: siguienteKey++, file, kind: 'anexo' as const }))]);
          if (ref.current) ref.current.value = '';
        }}
      />
    </Field>
  );
}

function FileIcon({ ext }: { ext: string }) {
  const isPdf  = ext === 'pdf';
  const isImg  = ['jpg', 'jpeg', 'png'].includes(ext);
  const color  = isPdf ? '#f87171' : isImg ? '#34d399' : '#60a5fa';
  const label  = isPdf ? 'PDF' : isImg ? 'IMG' : ext.toUpperCase() || 'DOC';
  return (
    <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '18', border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ color, fontSize: '0.6rem', fontWeight: 800 }}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn: React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const filterInp:  React.CSSProperties = { padding: '8px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.84rem', outline: 'none' };
const thStyle:    React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:         React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const overlay:    React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:   React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader:React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter:React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end' };
const inp:        React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:        React.CSSProperties = { ...inp, cursor: 'pointer' };
const closeBtn:   React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:  React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const linkBtn:    React.CSSProperties = { background: 'none', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem', padding: '3px 10px' };
const errorBox:   React.CSSProperties = { padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, color: '#fca5a5', fontSize: '0.82rem' };
const avisoBox:   React.CSSProperties = { padding: '8px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, color: '#fbbf24', fontSize: '0.78rem' };
const notaSugerencia: React.CSSProperties = { color: '#60a5fa', fontSize: '0.76rem', marginTop: -6 };
// Input de archivo que abre el botón "Reemplazar archivo" (no display:none, para que el clic funcione en todos los navegadores).
const archivoOculto:  React.CSSProperties = { position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' };
const saveBtn:    React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
